import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { format, startOfDay } from 'date-fns';
import type { CalendarView } from '@/components/calendar/CalendarToolbar';

export type DayLayout = 'horizontal' | 'vertical';

interface CalendarState {
  calendarDate: string; // ISO date string "YYYY-MM-DD"
  calendarView: CalendarView;
  dayLayout: DayLayout;
  showExtendedHours: boolean; // Show hours outside business hours
  /**
   * Compress the closed parts of the horizontal time axis so working hours get
   * the space (#1313). Off by default — it changes how the whole timeline reads,
   * so it should be something the user turns on.
   */
  compressClosedHours: boolean;
  setCalendarDate: (date: Date) => void;
  setCalendarView: (view: CalendarView) => void;
  setDayLayout: (layout: DayLayout) => void;
  setShowExtendedHours: (show: boolean) => void;
  setCompressClosedHours: (compress: boolean) => void;
}

export const useCalendarStore = create<CalendarState>()(
  persist(
    (set) => ({
      calendarDate: format(startOfDay(new Date()), 'yyyy-MM-dd'),
      calendarView: 'week',
      dayLayout: 'horizontal',
      showExtendedHours: false,
      compressClosedHours: false,
      setCalendarDate: (date) =>
        set({ calendarDate: format(startOfDay(date), 'yyyy-MM-dd') }),
      setCalendarView: (view) => set({ calendarView: view }),
      setDayLayout: (layout) => set({ dayLayout: layout }),
      setShowExtendedHours: (show) => set({ showExtendedHours: show }),
      setCompressClosedHours: (compress) => set({ compressClosedHours: compress }),
    }),
    {
      name: 'staff-portal-calendar',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        calendarDate: state.calendarDate,
        calendarView: state.calendarView,
        dayLayout: state.dayLayout,
        showExtendedHours: state.showExtendedHours,
        compressClosedHours: state.compressClosedHours,
      }),
    }
  )
);
